import { Editor } from '@tiptap/core';
import { StarterKit } from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import BubbleMenu, { type BubbleMenuOptions } from '@tiptap/extension-bubble-menu';
import DOMPurify from 'dompurify';
import { type CSSResultGroup, nothing, type PropertyValueMap, type PropertyValues } from 'lit';
import { css, html, LitElement } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import '../../library/elements/d-action';
import '../../library/elements/d-label';
import '../../library/editors/components/d-edit-html-toolbar';
import { styleMap } from 'lit/directives/style-map.js';
import { slideHtmlStyles } from 'src/content/screens/slide-html-styles';
import { classMap } from 'lit/directives/class-map.js';

/**
 */

@customElement('d-edit-html-for-slide')
export class DEditHtmlForSlide extends LitElement {
  static readonly styles: CSSResultGroup | undefined = [
    slideHtmlStyles,
    css`
      :host {
        display: flex;
        position: relative;
      }
      #tiptap-menu {
        background: white;
        padding: 12px;
        border-radius: 12px;
        visibility: hidden;
        opacity: 0;
        position: absolute;
        filter: drop-shadow(0 2px 10px hsla(1, 0%, 0%, 0.3));
        transition: all 0.3s;
        line-height: 0;
        z-index: 99999;
      }
      #placeholder {
        position: absolute;
        top: 0;
        width: 100%;
        box-sizing: border-box;
        pointer-events: none;
      }
      :host([header]) #placeholder {
        top: unset;
        bottom: 0;
      }
      #content::before {
        display: block;
        content: 'Tekst her'; /* i18n-exempt: placeholder-tekst via CSS ::before */
        opacity: 0.4;
        pointer-events: none;
      }
      :host([text-align='center']) #content::before {
        text-align: center;
      }
      :host([text-align='center']) ul,
      :host([text-align='center']) ol {
        list-style-position: inside;
        text-align: center;
        padding: 0;
      }
      :host([text-align='center']) ul p,
      :host([text-align='center']) ol p {
        display: inline;
      }
      :host([text-align='right']) #content::before {
        text-align: right;
      }
      :host([header]) #content::before {
        content: 'Overskrift her'; /* i18n-exempt: placeholder-tekst via CSS ::before */
        top: unset;
        bottom: 0.7cqi;
      }
      #editor .ProseMirror {
        outline: none;
      }
    `,
  ];
  @property({ type: String, attribute: 'layout-position', reflect: true })
  layoutPosition: 'full' | 'top' | 'right' | 'bottom' | 'left' = 'full';
  @property({ type: Boolean, reflect: true })
  header = false;
  @property({ type: String })
  textColor = 'black';
  @property({ type: String, attribute: 'text-align', reflect: true })
  textAlign = 'left';
  @property({ type: String })
  value = 'undefined';
  @query('#tiptap-menu')
  private menuElement?: HTMLDivElement;
  @query('#editor')
  private editorElement?: HTMLDivElement;
  private editor: Editor | undefined;
  @state()
  private _content = '';
  @state()
  private isEditorEmpty = false;
  @state()
  private _hasSelection = false;
  @property({ type: String })
  activeId = '';
  /**
   * Unique ID to prevent bubble menus for both header and text editors to show simultaneously
   */
  private menuKey = `bubble-menu-${Math.random().toString(36).slice(2, 11)}`;

  private _onFocus() {
    this.dispatchEvent(
      new CustomEvent('editor-activated', {
        detail: { editorId: this.menuKey },
        bubbles: true,
        composed: true,
      }),
    );
  }

  _valueChanged(newValue: string | undefined) {
    const v = newValue ?? '';
    const currentValue = this._content;
    if (currentValue !== v && this.editor) {
      this.editor.commands.setContent(v);
      return;
    }
    this._content = this.value;
  }

  setContent(value) {
    const config = {
      ALLOWED_TAGS: ['h1', 'h2', 'ul', 'ol', 'li', 'i', 'em', 'b', 'p', 'a', 'br', 'strong'],
      ALLOWED_ATTR: ['href', 'target', 'checked', 'class', 'style'],
      KEEP_CONTENT: true,
      ALLOW_DATA_ATTR: true,
      ALLOW_ARIA_ATTR: false,
    };

    this._content = DOMPurify.sanitize(value, config);
  }

  private toggleBulletList(isBulletList: boolean) {
    if (isBulletList) {
      this.editor?.chain().focus().setParagraph().run();
    } else {
      this.editor?.chain().focus().toggleBulletList().run();
    }
  }

  private toggleOrderedList(isBulletList: boolean) {
    if (isBulletList) {
      this.editor?.chain().focus().setParagraph().run();
    } else {
      this.editor?.chain().focus().toggleOrderedList().run();
    }
  }

  isDifferent(a, b) {
    if (a === '<p></p>' && b === '') {
      return false;
    }
    if (a === '' && b === '<p></p>') {
      return false;
    }
    return a !== b;
  }

  willUpdate(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    if (_changedProperties.has('value')) {
      this._valueChanged(this.value);
    }
  }

  protected get editorStyles() {
    return {
      color: this.textColor,
    };
  }

  private buttonClass(style: string) {
    return {
      active: this.editor !== undefined && this.editor.isActive(style),
    };
  }

  protected render() {
    const isBulletList = this.editor?.isActive('bulletList');
    const isOrderedList = this.editor?.isActive('orderedList');
    return html`
      <div id="tiptap-menu">
        ${!this.header
          ? html`
              <button
                class="bold ${classMap(this.buttonClass('bold'))}"
                @click=${() => this.editor?.chain().focus().toggleBold().run()}
              ></button>
            `
          : nothing}
        <button
          class="italic ${classMap(this.buttonClass('italic'))}"
          @click=${() => this.editor?.chain().focus().toggleItalic().run()}
        ></button>
        ${!this.header
          ? html`
              <button
                class="bullets ${classMap(this.buttonClass('bulletList'))}"
                @click=${() => this.toggleBulletList(isBulletList ?? false)}
              ></button>
              <button
                class="numbers ${classMap(this.buttonClass('orderedList'))}"
                @click=${() => this.toggleOrderedList(isOrderedList ?? false)}
              ></button>
            `
          : nothing}
        <button
          class="align left ${classMap(this.buttonClass('left'))}"
          @click=${() => this.editor?.chain().focus().setTextAlign('left').run()}
        ></button>
        <button
          class="align center ${classMap(this.buttonClass('center'))}"
          @click=${() => this.editor?.chain().focus().setTextAlign('center').run()}
        ></button>
        <button
          class="align right ${classMap(this.buttonClass('right'))}"
          @click=${() => this.editor?.chain().focus().setTextAlign('right').run()}
        ></button>
        ${this._hasSelection ? html`` : ''}
      </div>
      ${this._content === '' || this._content === '<p></p>'
        ? html`<div id="placeholder"><div id="content" style=${styleMap(this.editorStyles)}></div></div>`
        : nothing}
      <div id="editor" class="html editable" style=${styleMap(this.editorStyles)}></div>
    `;
  }

  /**
   * Checks if the other editor is active and removes selection to hide bubble menu
   */
  protected updated(_changedProperties: PropertyValues) {
    super.updated(_changedProperties);
    if (_changedProperties.has('activeId') && this.activeId !== this.menuKey && this.editor) {
      const { from } = this.editor.state.selection;
      this.editor.commands.setTextSelection(from);
    }
  }

  protected firstUpdated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    super.firstUpdated(_changedProperties);
    const bubbleMenuProps: Partial<BubbleMenuOptions> = {
      element: this.menuElement,
      pluginKey: this.menuKey,
      shouldShow: ({ state }) => {
        return !state.selection.empty;
      },
      options: {
        offset: 24,
        placement: 'right',
        flip: true,
        shift: true,
      },
    };
    if (this.closest('.slide')) {
      bubbleMenuProps.appendTo = this.closest('.slide') as HTMLDivElement;
    }
    this.editor = new Editor({
      element: this.editorElement,
      extensions: [
        StarterKit,
        BubbleMenu.configure(bubbleMenuProps),
        TextAlign.configure({
          types: ['heading', 'paragraph'],
        }),
      ],
      content: this._content,
      editorProps: {
        transformPastedHTML(html: string) {
          return html.replace(/<[^>]*>?/gm, '');
        },
      },
      onFocus: () => this._onFocus(),
      enablePasteRules: false,
      onSelectionUpdate: ({ editor }) => {
        this._hasSelection = !editor.state.selection.empty;
      },
      onTransaction: () => {
        if (this.editor) {
          this.isEditorEmpty = this.editor.isEmpty;
          const newValue = this.editor.getHTML();
          if (this.isDifferent(newValue, this._content)) {
            this.setContent(newValue);
            this.dispatchEvent(
              new CustomEvent('value-changed', {
                bubbles: true,
                composed: true,
                detail: {
                  value: this._content,
                },
              }),
            );
          }
        }
      },
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-edit-html-for-slide': DEditHtmlForSlide;
  }
}
